import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { isSameDay } from 'date-fns';
import { RoomsViewModel } from '../_rooms/rooms.viewmodel';
import { ISortOptions, ITheadSort, ListLayout } from '../../../global/tables/table-list-layout';
import { CommonModal } from '../../../global/modal/common/common-modal';
import DefaultButton from '../../../global/default-button/default-button';
import OptionsButton from '../../../global/options-button/options-button';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import UploadButton from '../shared/upload-button';
import { GenerateRoomsVM } from '../_rooms/_generate-rooms/generate-rooms.viewmodel';
import { DefaultCloseModalButton } from '../../../global/modal/common/common-close-modal-buttons';
import { CreateZoomMeetingsVM } from '../shared/_create-zoom-meetings/create-zoom-meetings.viewmodel';
import { ManageEventNavigation } from '../shared/navigation/navigation';
import { GenerateRoomsModal } from '../_rooms/_generate-rooms/_generate-rooms-modal/generate-rooms-modal.presentation';
import { CreateZoomMeetingsModal } from '../shared/_create-zoom-meetings/_create-zoom-meetings-modal/create-zoom-meetings-modal';
import { ZoomMeetingModal } from '../shared/connections-list/_zoom-meeting-modal/zoom-meeting-modal.presentation';
import { SortOrder } from '../../../../api/shared.dto';
import OENextPrevStepper from '../../../global/oe-next-prev-stepper/oe-next-prev-stepper';
import DateSelectionDropdown from './_date-selection-dropdown';
import { DateFnsFormats } from '../../../../infrastructure/date-formatter';
import { DateTimeFormatter } from '../../../../infrastructure/implementations/date-formatter';
import { RoomConnectionsListItem } from './_room-connections-list-item/_room-connections-list-item';
import { IConnectionDetailsDTO } from '../../../../api/connections.dao';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { ConnectionsViewModel } from './connections.viewmodel';
import { AllEventConfigurationModal } from './all-events-configuration-modal/all-events-configuration-modal';
import { MultiSelectOption } from '../../../global/multi-select-list/_multi-select-option';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../tracking-service/tracking-values';
import { getFormattedOperatorRoles } from '../../../../constants/entity-role-with-intl';
import { CreateZoomWebinarModal } from '../shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar-modal.presentation';
import { MeetingApiParams } from '../shared/connections-list/_connection-form/connection-form.viewmodel';
import { KnovioLiveStreamModal } from '../shared/connections-list/knovio-live-stream-modal/knovio-live-stream-modal.presentation';
import KnovioPreviewSpinner from '../../../global/spinner/knovio-preview-spinner';
import { MeetingConnectionsListItem } from './meeting-connections-list/_meeting-connections-list-item';

import './connections.scss';
import { MORE_BUTTON_TEXT, ROOM_TEXT } from '../../../../constants/common-labels.constants';

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

interface IProps {
    intl: IntlShape;
    roomsVM: RoomsViewModel;
    vm: ConnectionsViewModel;
    generateRoomsVM: GenerateRoomsVM;
    createZoomMeetingsVM: CreateZoomMeetingsVM;
}

const ConnectionsListPresentation = injectIntl(observer((props: IProps) => {
    const { vm, intl, roomsVM, generateRoomsVM, createZoomMeetingsVM } = props;
    const bulkOperatorsUpdateFormClosed = !!vm.bulkOperatorsUpdateFormPopupMap
        && vm.bulkOperatorsUpdateFormPopupMap?.getStatus() !== SubWindowStatus.Closed;

    const downloadAndTrackRoomConnections = (): void => {
        vm.downloadConnectionsCSV();
        vm.trackDownloadRoomConnections();
    };
    const downloadAndTrackOperators = (): void => {
        roomsVM.downloadOperatorsCSV();
        vm.trackDownloadOperatorsEvent();
    };

    const openDownloadAllEventConnections = (): void => {
        vm.setDownloadAllEventConnectionsModalOpen(true);
    };
    const closeDownloadAllEventConnections = (): void => {
        vm.setDownloadAllEventConnectionsModalOpen(false);
    };

    const theads: ITheadSort<string>[] = [
        {
            title: intl.formatMessage(ROOM_TEXT),
            width: '15%'
        },
        {
            title: <DateSelectionDropdown
                eventTimezone={vm.getEvent().timeZone}
                onSelect={vm.handleChangeFilterDate}
                filterDatesList={vm.filterDates}
                selectedDate={vm.selectedFilterDate}
            />,
            width: '20%'
        },
        {
            title: intl.formatMessage({
                id: 'oe.common.attendees',
                defaultMessage: 'Attendees',
                description: 'Label text'
            }),
            width: '20%'
        },
        {
            title: intl.formatMessage({
                id: 'oe.common.connections',
                defaultMessage: 'Connections',
                description: 'Label text'
            }),
            width: '20%',
            className: 'p-left-20px'
        },
        {
            title: (
                <MultiSelectOption
                    onClick={() => {
                        if (vm.isAllMeetingsSelected) {
                            vm.unSelectMeetingsIdsByRoom();

                        } else {
                            vm.selectMeetingsIdsByRoom();
                        }
                    }}
                    isSelected={vm.isAllMeetingsSelected}
                    isDisabled={bulkOperatorsUpdateFormClosed}
                />
            ),
            width: '5%'
        },
        {
            title: intl.formatMessage({
                id: 'oe.common.operators',
                defaultMessage: 'Operators',
                description: 'Label text'
            }),
            width: '15%'
        },
        { title: '', width: '10%' }
    ];
    const sortOptions: ISortOptions<string> = {
        sortOrder: SortOrder.Asc,
        sortField: 'startDateTime',
        sortRows: () => { /* TODO for the future */ },
        theads: theads
    };

    const roomsConnectionsList = vm.getRoomsConnections();
    const event = vm.getEvent();
    const loading = vm.getLoading();

    const isEmpty = !roomsConnectionsList?.length && !loading;
    roomsVM.setOnChangeCallback(() => vm.fetchRoomsWithConnections());
    const uploadOperatorsButtonProperties = roomsVM.composeUploadOperatorsButtonProperties();
    const createGenerateRoomConnectionsButtonProperties = createZoomMeetingsVM.composeGenerateRoomConnectionsButtonProperties();
    const roomsOptions = [
        hasPermission(Permission.CREATE_AND_ASSIGN_ROOM) && {
            label: 'Create & Assign Rooms',
            id: 'oe.rooms.createAndAssign',
            action: generateRoomsVM.showForm,
            disabled: !roomsVM.isPipelineReady()
        },
        hasPermission(Permission.CREATE_ZOOM_CONNECTIONS) && {
            label: createGenerateRoomConnectionsButtonProperties.text,
            id: 'oe.zoomMeetings.create',
            action: createZoomMeetingsVM.showConnectionsForm,
            disabled: createGenerateRoomConnectionsButtonProperties.disabled
        },
        hasPermission(Permission.EDIT_EVENT) && {
            label: intl.formatMessage({
                id: 'oe.control.editGuidelines',
                defaultMessage: 'Edit Guidelines',
                description: 'Edit general guidelines'
            }),
            action: () => {
                if (!vm.guidelinesFormSubWindow.create || vm.guidelinesFormSubWindow.create.getStatus() === SubWindowStatus.Closed) {
                    vm.showEventNotesPopup(true, OpenFrom.Connections);
                }
            }
        },
        hasPermission(Permission.CONNECTIONS_MANAGEMENT) && {
            label: 'Download all Event Connections',
            id: 'oe.connections.downloadEventConnectionsCSV',
            action: openDownloadAllEventConnections,
        },
        {
            label: 'Download Room Connections',
            id: 'oe.connections.downloadCSV',
            action: downloadAndTrackRoomConnections
        },
        {
            label: 'Download Operators',
            id: 'oe.operators.download',
            action: downloadAndTrackOperators
        },
        hasPermission(Permission.UPLOAD_OPERATORS) && {
            label: uploadOperatorsButtonProperties.text,
            id: 'oe.operators.upload',
            disabled: uploadOperatorsButtonProperties.disabled,
            action: () => roomsVM.setUploadOperatorsCSVModalOpen(true)
        }
    ].filter(Boolean);

    const getDayStringInEventTimezone = (date: Date) => dateTimeFormatter.dateToFormattedString(
        date,
        DateFnsFormats.StringifiedFullDayMonthNumericDateYear,
        vm.getEvent().timeZone
    );

    const isRoomEditable = hasPermission(Permission.EDIT_ROOM);
    const operatorRoles = getFormattedOperatorRoles(intl);
    const meetingParams: MeetingApiParams = {
        accountId: vm.accountId,
        eventId: vm.getEvent().eventId,
    };

    return (
        <div>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <ManageEventNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {!!vm.selectedMeetingIds.length && <DefaultButton
                            onClick={() => vm.openOperatorsBulkPopup()}
                            text={vm.staffMeetingsPopUpTitle}
                            modifier={['fat', 'confirm-safe']}
                            disabled={!roomsVM.isPipelineReady()}
                        />}
                        {hasPermission(Permission.CREATE_ROOM) && <DefaultButton
                            onClick={() => roomsVM.createRoom(OpenFrom.Connections)}
                            text={intl.formatMessage({
                                id: 'oe.rooms.new',
                                defaultMessage: '{prefix} New Room',
                                description: 'Button text'
                            }, {
                                prefix: '+'
                            })}
                            modifier={['fat', 'confirm-safe']}
                        />}
                        {!!roomsOptions.length && <OptionsButton
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={roomsOptions}
                        />}
                    </div>
                </div>
            </section>
            <section className='fixed-list-layout connections-block'>
                <div>
                    <ListLayout
                        sortOptions={sortOptions}
                        isDisabled={isEmpty}
                    >
                        {loading && <TableLoadingRow colSpan={theads.length} />}
                        {!loading && !!roomsConnectionsList.length &&
                            <tr className='sticky-date-title'>
                                <td colSpan={theads.length - 1} className='single-row-title-cell date-cell'>
                                    {vm.selectedFilterDate &&
                                        <span className='table-list-layout__inline-title'>
                                            {getDayStringInEventTimezone(vm.selectedFilterDate)}
                                        </span>}
                                </td>
                                <td className='navigation-controls'>
                                    <OENextPrevStepper
                                        datesArray={vm.filterDates}
                                        selectedHour={vm.selectedFilterDate}
                                        changeHour={vm.handleChangeDate}
                                    />
                                </td>
                            </tr>
                        }
                        {!loading && roomsConnectionsList.map(roomConnections => {
                            return (<React.Fragment key={roomConnections.roomId}>
                                <RoomConnectionsListItem
                                    {...roomConnections}
                                    key={roomConnections.roomId}
                                    eventTimezone={event.timeZone}
                                    showDates={!isSameDay(new Date(roomConnections.startDateTime), vm.selectedFilterDate)}
                                    toggleRoomMeetings={() => vm.toggleRoomMeetings(roomConnections)}
                                    editRoomConnection={vm.handleEditRoomConnection}
                                    cloneConnection={vm.handleCloneConnection}
                                    onEdit={roomsVM.updateRoom}
                                    showConfigurePassportForm={vm.showConfigurePassportForm}
                                    isSelected={roomsVM.isEditFormShown(roomConnections.roomId)}
                                    isRoomEditable={isRoomEditable}
                                    deleteRoomConnection={vm.handleDeleteRoomConnection}
                                    webinarReportsVM={vm.getWebinarReportsVM()}
                                    handleSelectRoom={() => vm.handleSelectRoom(roomConnections)}
                                    isRoomSelected={vm.isRoomSelected(roomConnections)}
                                    bulkOperatorsUpdateFormClosed={bulkOperatorsUpdateFormClosed}
                                    meetingsQuantity={vm.getMeetingIdsOnRoom(roomConnections).length}
                                    handleTroubleshoot={(connectionDetails: IConnectionDetailsDTO) => {
                                        vm.handleTroubleshoot(connectionDetails, OpenFrom.Connections);
                                    }}
                                    handleTrackingEvent={(eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CALL) => {
                                        vm.trackConnectionsEventMixPanel(eventName);
                                    }}
                                    trackHostJoinClick={(eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => {
                                        vm.trackHostJoinClick(eventName, OpenFrom.Connections);
                                    }}
                                    meetingParams={{ ...meetingParams, roomId: roomConnections.roomId }}
                                    shouldDisableDeleteButton={vm.shouldDisableDeleteButton}
                                />
                                {roomConnections.isExpanded && roomConnections.meetings && roomConnections.meetings.map((roomMeetingConnections, i) => {
                                    return (<MeetingConnectionsListItem
                                        vm={vm}
                                        key={roomMeetingConnections.meetingId}
                                        roomMeetingConnections={roomMeetingConnections}
                                        roomConnections={roomConnections}
                                        eventTimezone={event.timeZone}
                                        showDates={!isSameDay(new Date(roomConnections.startDateTime), vm.selectedFilterDate) ||
                                            !isSameDay(new Date(roomMeetingConnections.startDateTime), vm.selectedFilterDate)}
                                        isFirst={i === 0}
                                        bulkOperatorsUpdateFormClosed={bulkOperatorsUpdateFormClosed}
                                        operatorRoles={operatorRoles}
                                        meetingParams={{ ...meetingParams, meetingId: roomMeetingConnections.meetingId }}
                                    />);
                                })}
                            </React.Fragment>);
                        })}
                    </ListLayout>
                    {isEmpty &&
                        <div className='empty-list-message'>
                            <FormattedMessage
                                description='Meeting list empty text'
                                defaultMessage='There are no meetings scheduled in this event.'
                                id='oe.connections.emptyListText'
                            />
                        </div>
                    }
                </div>
                <CommonModal
                    title={intl.formatMessage({
                        id: 'oe.operators.upload',
                        defaultMessage: 'Upload Operators',
                        description: 'Label text'
                    })}
                    isOpen={roomsVM.getUploadOperatorsCSVModalOpen()}
                    onRequestClose={() => {
                        roomsVM.setUploadOperatorsCSVModalOpen(false);
                    }}
                    footer={(
                        <div className='buttons-block buttons-block--from-right'>
                            <DefaultButton
                                text={intl.formatMessage({
                                    id: 'oe.operators.upload',
                                    defaultMessage: 'Upload Operators',
                                    description: 'Label text'
                                })}
                                modifier={['fat', 'confirm-safe']}
                                onClick={roomsVM.uploadOperatorsCSV}
                                disabled={!roomsVM.getOperatorsCSVFileName()}
                            />
                            <DefaultCloseModalButton />
                        </div>
                    )}>
                    <div className='attendees-list__csv-modal-form'>
                        <UploadButton
                            fileName={roomsVM.getOperatorsCSVFileName()}
                            onChange={roomsVM.setOperatorsCSVFile}
                            className='attendees-list-option attendees-list-option__in-modal'
                        />
                    </div>
                </CommonModal>
                <GenerateRoomsModal vm={generateRoomsVM} />
                <CreateZoomMeetingsModal vm={createZoomMeetingsVM} />
                {vm.isDownloadAllEventConnectionsModalOpen && (
                    <AllEventConfigurationModal
                        accountId={vm.accountId}
                        eventId={vm.event.eventId}
                        onCancel={closeDownloadAllEventConnections}
                        intl={intl}
                    />
                )}

                <ZoomMeetingModal vm={vm.getZoomMeetingVM()} />
                <CreateZoomWebinarModal vm={vm.getWebinarMeetingVM()} />

                {vm.getKnovioLiveStreamModalVM() && <KnovioLiveStreamModal vm={vm.getKnovioLiveStreamModalVM()} />}
                {hasPermission(Permission.EDIT_KNOVIO_LIVE_STREAMS) &&
                    <CommonModal isOpen={vm.isKnovioPreCheckLoading} className='knovio-preFetch-loader-popover'>
                        <div className='knovio-preFetch-loader-popover__content-wrapper'>
                            <KnovioPreviewSpinner classname='knovioPreCheckLoading' />
                            <div className='oe-form-field__info'>
                                <FormattedMessage
                                    id='oe.knovio.prefetchLoader'
                                    defaultMessage='Loading'
                                    description='Info text'
                                />
                            </div>
                        </div>
                    </CommonModal>}
            </section>
        </div>
    );
}));

interface IListContainerProps {
    roomsVM: RoomsViewModel;
    vm: ConnectionsViewModel;
}


const ConnectionsListContainer = observer((props: IListContainerProps) => {
    const { vm, roomsVM } = props;

    vm.getConnectionDatesList();

    return <ConnectionsListPresentation vm={vm}
        roomsVM={roomsVM}
        generateRoomsVM={roomsVM.generateRoomsVM}
        createZoomMeetingsVM={roomsVM.createZoomMeetingsVM}
    />;
});

export { ConnectionsListContainer as ConnectionsList, ConnectionsListPresentation };
